import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ArrowUpRight,
  ChevronRight,
  LogIn,
  ShieldCheck,
} from "lucide-react";

import logo from "../assets/logo.png";

const translations = {
  home: "Home",
  how: "How It Works",
  earnings: "Earnings",
  collection: "Saree Collection",
  owners: "Shop Owners",
  faq: "FAQs",
  enquire: "Enquire",
  signIn: "Sign In",
  signUp: "Sign Up",
  admin: "Admin",
  explore: "Explore Vasthra",
  tagline: "Tradition meets opportunity",
  announcement: "Connecting saree businesses with resellers",
};

const sectionLinks = [
  { key: "home", href: "#home" },
  { key: "how", href: "#how-it-works" },
  { key: "earnings", href: "#earnings" },
  { key: "collection", href: "#collection" },
  { key: "owners", href: "#shop-owners" },
  { key: "faq", href: "#faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const location = useLocation();
  const t = translations;

  const closeMenu = () => setOpen(false);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const scrollToSection = (event, href) => {
    event.preventDefault();
    closeMenu();

    if (location.pathname !== "/") {
      window.location.href = `/${href}`;
      return;
    }

    if (href === "#home") {
      window.history.replaceState(null, "", "/");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const section = document.querySelector(href);

    if (section) {
      window.history.replaceState(null, "", href);
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const linkClass =
    "whitespace-nowrap text-xs font-medium text-[#51383f] transition-colors duration-200 hover:text-[#a51d47]";

  return (
    <header className="sticky top-0 z-50 border-b border-[#eee2d9] bg-[#fffdf9]/95 shadow-[0_4px_24px_rgba(74,7,24,0.05)] backdrop-blur-xl">
      <nav
        className="mx-auto flex min-h-[72px] max-w-[1400px] items-center justify-between gap-3 px-4 sm:px-6 lg:min-h-[78px] lg:px-10"
        aria-label="Main navigation"
      >
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group flex min-w-0 shrink-0 items-center gap-2.5 sm:gap-3"
          aria-label="Vasthra home"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#e5c28a]/50 bg-[#4a0718] shadow-sm transition-transform duration-300 group-hover:scale-105 sm:h-12 sm:w-12">
            {!logoFailed ? (
              <img
                src={logo}
                alt="Vasthra logo"
                onError={() => setLogoFailed(true)}
                className="h-full w-full object-contain p-1.5"
              />
            ) : (
              <span className="font-serif text-2xl font-bold text-[#e5c28a]">
                S
              </span>
            )}
          </span>

          <span className="min-w-0">
            <span className="block font-serif text-lg font-bold leading-tight text-[#4a0718] sm:text-xl">
              Vasthra
            </span>
            <span className="mt-1 hidden text-[8px] uppercase tracking-[0.12em] text-[#947052] sm:block sm:text-[9px]">
              {t.tagline}
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-4 xl:flex 2xl:gap-5">
          {sectionLinks.map((item) => (
            <a
              key={item.key}
              href={item.href}
              onClick={(event) => scrollToSection(event, item.href)}
              className={linkClass}
            >
              {t[item.key]}
            </a>
          ))}
        </div>

        <div className="hidden shrink-0 items-center gap-2 xl:flex">
          <Link
            to="/signin"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#e8d8ce] px-3 py-2.5 text-xs font-semibold text-[#4a0718] transition hover:border-[#a51d47] hover:bg-[#f8efeb]"
          >
            <LogIn size={14} />
            {t.signIn}
          </Link>

          <Link
            to="/signup"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#a51d47] px-4 py-3 text-xs font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#821535] hover:shadow-md"
          >
            {t.signUp}
            <ArrowUpRight size={15} />
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-2 xl:hidden">
          <button
            type="button"
            onClick={() => setOpen((previous) => !previous)}
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            aria-controls="mobile-navigation"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#eee2d9] text-[#4a0718] transition hover:bg-[#f5eae5] focus:outline-none focus:ring-2 focus:ring-[#a51d47]/30"
          >
            {open ? <X size={21} /> : <Menu size={21} />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          id="mobile-navigation"
          className="absolute left-0 right-0 top-full max-h-[calc(100dvh-80px)] overflow-y-auto border-t border-[#eee2d9] bg-[#fffdf9] px-4 pb-6 pt-4 shadow-xl xl:hidden sm:px-6"
        >
          <div className="mx-auto flex max-w-[700px] flex-col">
            <p className="mb-3 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-[#a27848]">
              {t.explore}
            </p>

            {sectionLinks.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(event) => scrollToSection(event, item.href)}
                className="flex items-center justify-between border-b border-[#f0e5df] px-3 py-3.5 text-sm font-medium text-[#51383f] transition hover:bg-[#faf4ef] hover:text-[#a51d47]"
              >
                {t[item.key]}
                <ChevronRight size={16} className="text-[#b8a29a]" />
              </a>
            ))}

            <a
              href="#contact"
              onClick={(event) => scrollToSection(event, "#contact")}
              className="mt-4 flex items-center justify-center rounded-xl border border-[#e8d8ce] py-3 text-sm font-semibold text-[#4a0718] transition hover:bg-[#f8efeb]"
            >
              {t.enquire}
            </a>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <Link
                to="/signin"
                onClick={closeMenu}
                className="flex items-center justify-center gap-2 rounded-xl border border-[#e8d8ce] py-3.5 text-sm font-semibold text-[#4a0718] transition hover:bg-[#f8efeb]"
              >
                <LogIn size={16} />
                {t.signIn}
              </Link>

              <Link
                to="/signup"
                onClick={closeMenu}
                className="flex items-center justify-center gap-2 rounded-xl bg-[#a51d47] py-3.5 text-sm font-semibold text-white transition hover:bg-[#821535]"
              >
                {t.signUp}
                <ArrowUpRight size={16} />
              </Link>
            </div>

            <Link
              to="/admin/dashboard"
              onClick={closeMenu}
              className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-[#e5c28a] py-3.5 text-sm font-semibold text-[#4a0718] transition hover:bg-[#f8efeb]"
            >
              <ShieldCheck size={16} />
              {t.admin}
            </Link>

            <p className="mt-4 text-center text-[11px] leading-5 text-[#947d7e]">
              {t.tagline}
            </p>
          </div>
        </div>
      )}
    </header>
  );
}
